/**
 * Pathways — where this person's work can go next.
 *
 * The screen this replaces was the legacy Phase2, which asked the model for
 * "4 pathways" from a job TITLE and fell back to a hand-written table. Two
 * things were wrong with it. It rendered off a `job` variable the refactor had
 * already deleted, so it never rendered at all. And when it did, the pathways
 * came from a fixed list — which is why every résumé produced the same four
 * destinations.
 *
 * Destinations are now derived per person, from three inputs that all differ
 * between two people with the same job title:
 *
 *   adjacency   O*NET's own relatedness graph for the occupation we resolved
 *   exposure    each destination scored by the same task-level scorer, so a
 *               move is only offered when it actually lowers exposure
 *   overlap     how much of THIS person's skill vector the destination reuses
 *
 * The emerging layer is separate and honestly labelled. Roles like Forward
 * Deployed Engineer do not exist in O*NET — the survey is years behind the
 * market, which is the whole premise of the product — so they are matched on
 * skills and shown as market roles, never dressed up as government data.
 */
import { useEffect, useState } from "react";
import { PANEL, TEAL, CREAM, CREAM60, CREAM30, BORDER, AMBER, GREEN, NAVY } from "../utils/constants";
import { scoreOccupation, exposureLabel } from "../utils/exposure";
import { loadDemand, demandFor, demandVerdict, fmtCount, fmtGrowth } from "../utils/demand";
import PulseLoader from "./PulseLoader";
import { EMERGING } from "../data/emerging";

/**
 * Roles the market has, that O*NET does not yet.
 *
 * Each is anchored on skill IDs from the same taxonomy the rest of the app
 * uses, so "why this fits you" is computed, not asserted. `anchor` names the
 * closest O*NET occupation for exposure scoring; where none is close enough the
 * exposure is marked as an estimate rather than invented precisely.
 */

const MOVE = {
  adjacent: { label: "Adjacent", note: "Most of your skills carry over" },
  elevate: { label: "Elevate", note: "Same field, more ownership" },
  pivot: { label: "Pivot", note: "Real distance — worth planning for" },
};

const overlapOf = (mine, theirs) => {
  const keys = Object.keys(theirs || {});
  if (!keys.length || !mine?.size) return 0;
  let hit = 0;
  for (const k of keys) if (mine.has(k) && theirs[k] >= 3.2) hit++;
  const strong = keys.filter((k) => theirs[k] >= 3.2).length || 1;
  return Math.round((hit / strong) * 100);
};

export default function Pathways({ profile, occupationCode, currentScore, skills, onChoose, onBack }) {
  const [rows, setRows] = useState(null);
  const [emerging, setEmerging] = useState([]);

  useEffect(() => {
    let dead = false;
    (async () => {
      const [relMod, taskMod, occMod, demandData] = await Promise.all([
        import("../data/taxonomy/related.json"),
        import("../data/taxonomy/tasks.json"),
        import("../data/taxonomy/occupations.json"),
        loadDemand(),
      ]);
      const related = relMod.default, tasksByCode = taskMod.default;
      const byCode = new Map(occMod.default.map((o) => [o.code, o]));

      const me = byCode.get(occupationCode);
      const mySkills = new Set(
        Object.entries(me?.skills || {}).filter(([, v]) => v >= 3.2).map(([k]) => k)
      );
      const here = currentScore?.exposure ?? null;

      const neighbours = (related[occupationCode] || [])
        .filter((r) => r.tier !== "Supplemental")
        .map((r) => {
          const occ = byCode.get(r.code);
          if (!occ) return null;
          const s = scoreOccupation(tasksByCode[r.code] || []);
          if (!s) return null;
          const overlap = overlapOf(mySkills, occ.skills);
          const delta = here == null ? 0 : Math.round((here - s.exposure) * 10) / 10;
          const move = overlap >= 70 ? "adjacent" : overlap >= 45 ? "elevate" : "pivot";
          const dem = demandFor(demandData, r.code);
          return {
            code: r.code, title: occ.title, exposure: s.exposure, judgment: s.judgment,
            premium: s.premium, overlap, delta, move,
            inferred: Boolean(occ.skillsInferred),
            demand: dem,
            verdict: demandVerdict(dem, s.exposure),
          };
        })
        .filter(Boolean)
        // Everything in the neighbourhood is shown, ranked by how much exposure
        // the move removes. Hiding the ones that do not help looked tidier and
        // was worse: for an IT project manager, EVERY adjacent role scores at or
        // above where they already are, and that is the most useful thing the
        // screen can tell them. An empty list says "we found nothing"; a ranked
        // list ending in red says "the neighbourhood is not the answer" — which
        // is the actual finding, and the reason the emerging roles below exist.
        .sort((a, b) => b.delta * 0.6 + b.overlap / 100 - (a.delta * 0.6 + a.overlap / 100))
        .slice(0, 6);

      // Emerging roles are gated on this person's own evidence, so they appear
      // when they fit and stay absent when they do not.
      const evidence = ` ${[profile?.taskText || "", (profile?.skills || []).join(" "),
        (profile?.roles || []).map((r) => r.title).join(" ")].join(" ").toLowerCase()} `;
      const em = EMERGING.map((e) => {
        const signalHits = e.signals.filter((w) => evidence.includes(w)).length;
        const skillHits = e.fitSkills.filter((id) => mySkills.has(id)).length;
        const anchorScore = scoreOccupation(tasksByCode[e.anchor] || []);
        return {
          ...e, signalHits, skillHits,
          fit: Math.round(((skillHits / e.fitSkills.length) * 0.6 + Math.min(1, signalHits / 4) * 0.4) * 100),
          exposure: anchorScore?.exposure ?? null,
          // An emerging role borrows an O*NET occupation for its activity data,
          // and six of those anchors are among the most common resolutions in
          // this cohort. When the anchor IS the person's occupation this is not
          // a move — it is a redesign of the job they already hold. Said out
          // loud rather than hidden, because it is the more interesting finding.
          redesign: e.anchor === occupationCode,
        };
      })
        .filter((e) => e.fit >= 40)
        .sort((a, b) => b.fit - a.fit)
        .slice(0, 3);

      if (!dead) { setRows(neighbours); setEmerging(em); }
    })();
    return () => { dead = true; };
  }, [occupationCode, currentScore, profile]);

  if (!rows) return <PulseLoader text="Tracing where this work goes next…" />;

  const Card = ({ r }) => (
    <button
      onClick={() => onChoose?.(r)}
      style={{
        textAlign: "left", width: "100%", background: PANEL, border: `1px solid ${BORDER}`,
        borderLeft: `3px solid ${r.delta >= 1 ? GREEN : r.delta > 0 ? TEAL : AMBER}`, borderRadius: 10,
        padding: 16, marginBottom: 10, cursor: "pointer", color: CREAM,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 3 }}>{r.title}</div>
          <div style={{ fontSize: 11, color: CREAM30, fontFamily: "'JetBrains Mono', monospace" }}>
            {MOVE[r.move].label.toUpperCase()} · {MOVE[r.move].note}
            {r.inferred ? " · skills inferred from related roles" : ""}
          </div>
        </div>
        <div style={{ textAlign: "right", minWidth: 120 }}>
          <div style={{ fontSize: 18, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace",
                        color: r.delta > 0 ? GREEN : AMBER }}>
            {r.delta > 0 ? `−${r.delta}` : `+${Math.abs(r.delta)}`}
          </div>
          <div style={{ fontSize: 10, color: CREAM30 }}>
            {r.delta > 0 ? "less exposed than today" : "no safer than today"}
          </div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 16, marginTop: 10, fontSize: 11, color: CREAM60,
                    fontFamily: "'JetBrains Mono', monospace", flexWrap: "wrap" }}>
        <span>skills reused <strong style={{ color: TEAL }}>{r.overlap}%</strong></span>
        <span>exposure {r.exposure} {exposureLabel(r.exposure)}</span>
        <span>judgment {r.judgment}</span>
        {r.demand?.annualOpenings != null && (
          <span title="Projected annual average openings over ten years — BLS, not actual postings">
            openings <strong style={{ color: CREAM }}>{fmtCount(r.demand.annualOpenings)}</strong>/yr
          </span>
        )}
        {fmtGrowth(r.demand?.growthPct) && (
          <span style={{ color: r.demand.growthPct < 0 ? AMBER : GREEN }}>
            {fmtGrowth(r.demand.growthPct)} 10yr
          </span>
        )}
      </div>
      {r.verdict && (
        <div style={{ marginTop: 9, fontSize: 12, lineHeight: 1.5,
                      color: r.verdict.tone === "warn" ? AMBER : GREEN }}>
          {r.verdict.text}
        </div>
      )}
    </button>
  );

  // ── ADR-042, made reachable ───────────────────────────────────────────────
  // `computeGap` has understood a self-anchored destination as a REDESIGN rather
  // than a move since ADR-042, and Learn.jsx has rendered "what changes if you
  // rebuild your role as X" for just as long. Neither had a door: the only way
  // in was an emerging role that happened to be anchored to your own occupation,
  // which is an accident, not a feature.
  //
  // It also answers the objection the external reviewer raised loudest, in the
  // words of the person raising it: "I am not necessarily trying to change
  // occupations. I want to redesign my current role so that my judgment,
  // influence and leadership become more valuable."
  //
  // It is listed FIRST, deliberately. Leading with the exits implies leaving is
  // the answer, and for most people it is not.
  const atRisk = currentScore?.premiumWarnings || 0;
  const Redesign = () => (
    <button
      onClick={() => onChoose?.({ code: occupationCode, title: profile?.chosenTitle, redesign: true })}
      style={{
        textAlign: "left", width: "100%", background: `${TEAL}0A`, border: `1px solid ${TEAL}40`,
        borderLeft: `3px solid ${TEAL}`, borderRadius: 10, padding: 18, marginBottom: 26,
        cursor: "pointer", color: CREAM,
      }}
    >
      <div style={{ fontSize: 11, color: TEAL, fontWeight: 700, letterSpacing: 1,
                    textTransform: "uppercase", fontFamily: "'JetBrains Mono', monospace",
                    marginBottom: 6 }}>
        Start here
      </div>
      <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>
        Maximize your current role
      </div>
      <div style={{ fontSize: 13, color: CREAM60, lineHeight: 1.6 }}>
        {atRisk > 0
          ? <>Not every answer is a different job. {atRisk === 1 ? "One task in your role is" : `${atRisk} tasks in your role are`}{" "}
             work AI cannot fully do but nobody pays extra for any more — and that is something you
             can act on without moving anywhere.</>
          : <>Not every answer is a different job. The same seat, rebuilt around the work that is
             still scarce, is usually the faster move — and the one you can start this week.</>}
      </div>
      <div style={{ fontSize: 12, color: TEAL, marginTop: 10, fontWeight: 600 }}>
        Rebuild {profile?.chosenTitle ? `“${profile.chosenTitle}”` : "this role"} in place →
      </div>
    </button>
  );

  return (
    <div style={{ animation: "fadeUp 0.4s ease" }}>
      <Redesign />

      <h2 style={{ color: CREAM, fontSize: 22, margin: "0 0 4px", fontFamily: "'Space Grotesk', sans-serif" }}>
        Where these skills can take you
      </h2>
      <p style={{ color: CREAM60, fontSize: 14, margin: "0 0 20px" }}>
        Ranked by how much exposure the move removes and how much of your work it reuses.
        Nothing here is a sideways step into the same risk.
      </p>

      {rows.length > 0 && rows.every((r) => r.delta <= 0) && (
        <div style={{ background: `${AMBER}10`, border: `1px solid ${AMBER}40`, borderRadius: 10,
                      padding: 16, marginBottom: 18, color: CREAM60, fontSize: 13, lineHeight: 1.6 }}>
          <strong style={{ color: AMBER }}>Every adjacent role is at least as exposed as yours.</strong><br />
          That is a finding, not an empty result. Moving sideways inside this neighbourhood buys
          nothing — the move worth making is out of it. Keep reading.
        </div>
      )}

      {rows.map((r) => <Card key={r.code} r={r} />)}

      {emerging.length > 0 && (
        <>
          <div style={{ fontSize: 12, color: AMBER, fontWeight: 600, margin: "26px 0 4px",
                        textTransform: "uppercase", letterSpacing: 1,
                        fontFamily: "'JetBrains Mono', monospace" }}>
            Roles the data hasn't caught up with
          </div>
          <p style={{ color: CREAM30, fontSize: 12, margin: "0 0 12px", lineHeight: 1.6 }}>
            These are not in the government occupation data — the survey runs years behind the market,
            which is the whole reason this product exists. Matched on your skills, and labelled honestly.
          </p>
          {emerging.map((e) => (
            <button key={e.id} onClick={() => onChoose?.({ ...e, code: e.anchor, emerging: true })}
              aria-label={`${e.title} — ${e.redesign ? "a redesign of your current role" : `${e.fit}% fit`}`}
              style={{ textAlign: "left", width: "100%", background: "transparent",
                       border: `1px dashed ${AMBER}55`, borderRadius: 10, padding: 16,
                       marginBottom: 10, cursor: "pointer", color: CREAM }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                <div style={{ fontSize: 15, fontWeight: 600 }}>{e.title}</div>
                <div style={{ fontSize: 12, color: AMBER, fontFamily: "'JetBrains Mono', monospace" }}>
                  {e.redesign ? "same seat, new work" : `${e.fit}% fit`}
                </div>
              </div>
              <div style={{ fontSize: 13, color: CREAM60, marginTop: 6, lineHeight: 1.55 }}>{e.blurb}</div>
              <div style={{ fontSize: 11, color: CREAM30, marginTop: 8,
                            fontFamily: "'JetBrains Mono', monospace" }}>
                why you · {e.why}
              </div>
              {e.redesign && (
                <div style={{ fontSize: 12, color: AMBER, marginTop: 8, lineHeight: 1.55 }}>
                  This one is anchored to the occupation you are already in. Not a move — the same
                  seat, rebuilt around work that did not exist when the job was last surveyed.
                </div>
              )}
            </button>
          ))}
        </>
      )}

      <p style={{ fontSize: 11, color: CREAM30, lineHeight: 1.6, margin: "20px 0 0", maxWidth: "64ch" }}>
        Openings are <strong style={{ color: CREAM60 }}>projected</strong> annual averages from the
        US Bureau of Labor Statistics, not counts of live postings — per-occupation actuals are not
        published free. Read them against the exposure score beside them: a role still hiring for
        work that is being automated is the most expensive kind of opportunity.
      </p>

      <button onClick={onBack}
        style={{ background: "none", border: `1px solid ${CREAM30}`, color: CREAM60, marginTop: 18,
                 padding: "8px 16px", borderRadius: 6, cursor: "pointer", fontSize: 13 }}>
        ← Back to your exposure
      </button>
    </div>
  );
}
